import { CapacitorHttp } from "@capacitor/core";
import type { AxiosAdapter } from "axios";

/**
 * Custom Axios adapter that uses Capacitor's native HTTP plugin
 * instead of XMLHttpRequest/fetch on native platforms (iOS/Android).
 *
 * This bypasses WKWebView networking restrictions and uses
 * native URLSession on iOS, which provides better reliability
 * and performance for API requests.
 *
 * Falls back to fetch for FormData (file uploads) since
 * CapacitorHttp doesn't handle multipart/form-data well.
 */
export const capacitorHttpAdapter: AxiosAdapter = async (config) => {
	try {
		// Detect FormData - use native fetch instead
		if (config.data instanceof FormData) {
			// Build full URL
			const url = `${config.baseURL || ""}${config.url || ""}`;

			// Prepare fetch options
			const fetchOptions: RequestInit = {
				method: config.method?.toUpperCase() || "POST",
				body: config.data,
				headers: {
					// Don't set Content-Type for FormData - browser will set it with boundary
					...Object.fromEntries(
						Object.entries(config.headers || {}).filter(
							([key]) => key.toLowerCase() !== "content-type",
						),
					),
				},
				signal: config.signal as AbortSignal | undefined,
			};

			// Use native fetch
			const response = await fetch(url, fetchOptions);

			// Parse response
			let data: unknown;
			const contentType = response.headers.get("content-type");
			if (contentType?.includes("application/json")) {
				data = await response.json();
			} else {
				data = await response.text();
			}

			// Check for HTTP errors
			if (!response.ok) {
				const error = new Error(
					`HTTP ${response.status}: ${response.statusText}`,
				) as Error & {
					response?: {
						status: number;
						statusText: string;
						data: unknown;
						headers: Record<string, string>;
					};
					config?: typeof config;
				};
				error.response = {
					status: response.status,
					statusText: response.statusText,
					data,
					headers: Object.fromEntries(response.headers.entries()),
				};
				error.config = config;
				throw error;
			}

			return {
				data,
				status: response.status,
				statusText: response.statusText,
				headers: Object.fromEntries(response.headers.entries()),
				config,
				request: {},
			};
		}

		// Use native HTTP for JSON/regular requests
		const response = await CapacitorHttp.request({
			url: `${config.baseURL || ""}${config.url || ""}`,
			method: config.method?.toUpperCase() || "GET",
			headers: config.headers as Record<string, string>,
			data: config.data,
		});

		// Check for HTTP errors (status >= 400)
		// CapacitorHttp doesn't throw on error status codes, so we must check manually
		if (response.status >= 400) {
			const error = new Error(`HTTP ${response.status}: Error`) as Error & {
				response?: {
					status: number;
					statusText: string;
					data: unknown;
					headers: Record<string, string>;
				};
				config?: typeof config;
			};
			error.response = {
				status: response.status,
				statusText: `${response.status}`,
				data: response.data,
				headers: response.headers,
			};
			error.config = config;
			throw error;
		}

		return {
			data: response.data,
			status: response.status,
			statusText: `${response.status}`,
			headers: response.headers,
			config,
			request: {},
		};
	} catch (error) {
		console.error("❌ [CAPACITOR] Native HTTP request failed:", {
			url: config.url,
			method: config.method,
			error,
		});
		throw error;
	}
};
